import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { DialogFormComponent } from '@modules/forms/components/dialog-form/dialog-form.component';
import { FormOutputs } from '@modules/forms/models/form-outputs';
import { ExaminationFormResponse } from '@pixacare/dtos';
import { DialogService } from '@services/dialog.service';
import { ActionsService } from '@shared/models/helpers/actions-service';
import { MenuAction } from '@shared/models/menu-actions/menu-action';
import { MenuActionId } from '@shared/models/menu-actions/menu-action-id.enum';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { EMPTY, first, of, switchMap } from 'rxjs';
import { ExaminationDialogComponent } from '../components/examination-dialog/examination-dialog.component';
import { ExaminationDisplay } from '../models/examination-display';
import { ExaminationsNavigationService } from './examination-navigation.service';
import { ExaminationsService } from './examinations.service';

@Injectable()
export class ExaminationActionsService extends ActionsService {
  private readonly translocoService = inject(TranslocoService);

  actions: MenuAction<ExaminationDisplay>[] = [
    {
      // works only in a examination navigation service context
      id: MenuActionId.EXAMINATION_CREATE,
      label: this.translocoService.translate('Nouvel examen'),
      icon: '@tui.plus',
      condition: () => !!this.examinationsNavigationService,
      condition$: () => this.examinationsService.canWriteExamination$,
      execute: (): void => {
        {
          this.examinationsNavigationService.examinationForm$.pipe(first()).subscribe((examinationForm) => {
            this.addExamination(examinationForm);
          });
        }
      },
    },
    {
      id: MenuActionId.EXAMINATION_OPEN,
      label: this.translocoService.translate('Ouvrir en plein écran'),
      icon: '@tui.maximize-2',
      condition$: () => of(!this.isDialog),
      condition: (examination) => !!examination,
      execute: (examination: ExaminationDisplay): void => {
        this.examinationsService.props$.pipe(first()).subscribe(({ clientCode, departmentId }) => {
          this.openExaminations({
            clientCode,
            patientId: examination.patientId,
            departmentId,
            examinationId: examination.id,
            examinationFormId: examination.examinationFormId,
          });
        });
      },
    },
    {
      id: MenuActionId.EXAMINATION_EDIT,
      label: this.translocoService.translate('Modifier'),
      icon: '@tui.pencil',
      condition$: () => this.examinationsService.canWriteExamination$,
      condition: (examination) => !!examination,
      execute: (examination: ExaminationDisplay): void => {
        this.examinationsService.examinationForms$.pipe(first()).subscribe((examinationForms) => {
          const examinationForm = examinationForms[examination.examinationFormId];
          this.updateExamination(examination, examinationForm);
        });
      },
    },
    {
      id: MenuActionId.EXAMINATION_DELETE,
      label: this.translocoService.translate('Supprimer'),
      icon: '@tui.trash',
      condition$: () => this.examinationsService.canWriteExamination$,
      condition: (examination) => !!examination,
      execute: (examination: ExaminationDisplay): void => {
        this.dialogService
          .openConfirm(this.translocoService.translate("Supprimer l'examen"), {
            content: this.translocoService.translate('Êtes-vous sûr de vouloir supprimer cet examen ?'),
            yes: this.translocoService.translate('Supprimer'),
            isDestructive: true,
            no: this.translocoService.translate('Annuler'),
          })
          .subscribe((confirmed) => {
            if (confirmed) {
              this.examinationsService.deleteExamination(examination.id).subscribe();
            }
          });
      },
    },
  ];

  private readonly examinationsService = inject(ExaminationsService);
  private readonly examinationsNavigationService = inject(ExaminationsNavigationService, { optional: true });
  private readonly dialogService = inject(DialogService);
  private readonly isDialog = !!inject(POLYMORPHEUS_CONTEXT, { optional: true });

  addExamination(examinationForm: ExaminationFormResponse): void {
    this.dialogService
      .openComponentWithCloseConfirmation<DialogFormComponent, FormOutputs>(DialogFormComponent, {
        data: {
          form: examinationForm.form,
        },
      })
      .pipe(
        switchMap(({ answers: formAnswers }) => {
          if (!formAnswers || !Object.keys(formAnswers).length) {
            return EMPTY;
          }

          return this.examinationsService.createExamination(examinationForm.id, formAnswers);
        }),
      )
      .subscribe();
  }

  updateExamination(examination: ExaminationDisplay, examinationForm: ExaminationFormResponse): void {
    this.dialogService
      .openComponentWithCloseConfirmation<DialogFormComponent, FormOutputs>(DialogFormComponent, {
        label: this.translocoService.translate("Modifier l'examen"),
        size: 'm',
        data: {
          form: examinationForm.form,
          formAnswer: examination.formAnswer,
        },
      })
      .pipe(
        switchMap(({ answers: formAnswers }) => {
          if (!formAnswers || !Object.keys(formAnswers).length) {
            return EMPTY;
          }

          return this.examinationsService.updateExamination(examination.id, formAnswers);
        }),
      )
      .subscribe();
  }

  openExaminations({
    clientCode,
    patientId,
    departmentId,
    examinationId,
    examinationFormId,
  }: {
    clientCode: string;
    patientId: number;
    departmentId?: number;
    examinationId?: number;
    examinationFormId?: number;
  }): void {
    this.dialogService
      .openComponent(ExaminationDialogComponent, {
        label: this.translocoService.translate('Examens du patient'),
        size: 'm',
        data: {
          clientCode,
          patientId,
          departmentId,
          examinationId,
          examinationFormId,
        },
      })
      .subscribe();
  }
}
